import React, { FC, useEffect } from 'react';
import { Redirect } from 'react-router-dom';

import { EvError, EvFlexColumn, EvSpinner } from '@evinced-private/ui-common';

import { LABEL_MANAGEMENT_PAGE_DAID } from 'data-automation-ids/LabelManagementAutomationIds';
import { useLabelManagementStore } from 'src/apps/dashboard/stores/labelManagementStore';
import { isConfigurationReady } from 'src/common/helpers/ConfigurationHelpers';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { ProductType } from 'src/common/interfaces/Tenant';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';
import { useUserTenant } from 'src/common/providers/userTenantProvider/UserTenantProvider';

import { PAGE_HEADER_LABELS } from './components/page-header/LabelManagementPageHeader.labels';
import { LabelManagementPageHeader, LabelPanel, LabelsTable } from './components';
import { PAGE_LABELS } from './LabelManagementPage.labels';

export const LabelManagementPage: FC = () => {
	const { getProductIdByType, hasAdminPermissions } = useUserTenant();
	const { configuration } = useConfiguration();
	const {
		labels,
		isLoading,
		error,
		isPanelOpen,
		panelMode,
		activeLabel,
		isSaving,
		fetchLabels,
		saveLabel,
		openAddPanel,
		openEditPanel,
		closePanel,
		reset
	} = useLabelManagementStore();

	const productId = getProductIdByType(ProductType.PLATFORM);

	useEffect(() => {
		if (productId) {
			fetchLabels(productId);
		}
	}, [productId, fetchLabels]);

	useEffect(() => {
		return () => {
			reset();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const isLabelsManagementEnabled =
		configuration?.PLATFORM?.DEV_TOGGLES?.ENABLE_LABELS_MANAGEMENT ?? false;

	if (!hasAdminPermissions()) {
		return <Redirect to={RoutesHelper.getUnauthorized403Page()} />;
	}

	if (!isConfigurationReady(configuration)) {
		return <EvSpinner srMessage={PAGE_LABELS.LOADING} />;
	}

	if (!isLabelsManagementEnabled) {
		return <Redirect to={RoutesHelper.getUnauthorized403Page()} />;
	}

	if (isLoading) {
		return <EvSpinner srMessage={PAGE_LABELS.LOADING} />;
	}

	if (error) {
		return <EvError error={PAGE_LABELS.ERROR} />;
	}

	return (
		<EvFlexColumn dataAutomationId={LABEL_MANAGEMENT_PAGE_DAID} padding="LARGE" gap="XLARGE">
			<LabelManagementPageHeader
				title={PAGE_HEADER_LABELS.TITLE}
				subtitle={PAGE_HEADER_LABELS.SUBTITLE}
			/>
			<LabelsTable labels={labels} onEdit={openEditPanel} onCreateLabel={openAddPanel} />
			{isPanelOpen && productId && (
				<LabelPanel
					activeLabel={activeLabel}
					panelMode={panelMode}
					productId={productId}
					isSaving={isSaving}
					onSave={saveLabel}
					onClose={closePanel}
				/>
			)}
		</EvFlexColumn>
	);
};
